/**
 * Non-interface helper methods for LoaderNormalJson
 * Contains private helpers and methods not part of ILoader interface
 *
 * These methods are attached to LoaderNormalJson.prototype in the main file
 */

import { LogLevel, Logger } from '../../../../Arshu/dist/logger/logger.js';
import { ResourceStatistics } from '../../../../Assembler/dist/model/index.js';
import { JsonArray, JsonObject } from '../../../../Assembler/dist/app/json/jsonValue.js';
import { JsonUtility } from '../../../../Assembler/dist/app/jsonUtility.js';
import { CommonUtil } from '../../../../Assembler/dist/common/commonUtil.js';
import { GenerateHtmlJson } from '../../base/loader/generateHtmlJson.js';
import type { LoaderNormalJson } from './loaderNormalJson.js';
import type { IConfig } from '../../../../Assembler/dist/config/iConfig.js';
import { ArtifactKind } from '../../../../Assembler/dist/config/iConfig.js';
import type { DynamicConfigMap } from '../../../../Assembler/dist/registry/dynamicConfigMap.js';

type PlainJsonObject = { [key: string]: unknown };

/**
 * Accessor methods to be attached to LoaderNormalJson prototype
 */
export const accessorMethods = {
    // #region Loading Related (Non-Interface)

    /**
     * Checks if a template exists by using getTemplate and comparing with default HtmlJson
     */
    hasTemplate(this: LoaderNormalJson, appSite: string, currentTemplateName: string, appFileOrPlaceholderOrSlotholderName: string, appView: string | null = null): boolean {
        const { htmlJson } = this.getTemplate(appSite, currentTemplateName, appFileOrPlaceholderOrSlotholderName, appView);

        if (!htmlJson || htmlJson.size === 0) {
            return false;
        }

        if (htmlJson.size === 1) {
            const legacyHtml = htmlJson.get('001_Html');
            if (!legacyHtml.isNull) {
                return legacyHtml.asString() !== this._config!.defaultReplaceHtml;
            }
        }

        return true;
    },

    // #endregion

    // #region Configuration (Non-Interface)

    /**
     * Skip patterns for Dynamic View handlers (comma-delimited or "*").
     * Mirrors C# `LoaderNormalJson_Accessors.cs : public string? SkipDynamicView`.
     * Attached to LoaderNormalJson.prototype via Object.defineProperties; `this` is bound to the instance at call time.
     */
    get skipDynamicView(): string | null { return (this as unknown as LoaderNormalJson)._skipDynamicView; },
    set skipDynamicView(value: string | null) { (this as unknown as LoaderNormalJson)._skipDynamicView = value; },

    /**
     * Skip patterns for Dynamic Data handlers (comma-delimited or "*").
     * Mirrors C# `LoaderNormalJson_Accessors.cs : public string? SkipDynamicData`.
     */
    get skipDynamicData(): string | null { return (this as unknown as LoaderNormalJson)._skipDynamicData; },
    set skipDynamicData(value: string | null) { (this as unknown as LoaderNormalJson)._skipDynamicData = value; },

    /**
     * Gets SearchAppSites for the specified AppSite
     */
    getSearchAppSitesForAppSite(this: LoaderNormalJson, appSite: string): string {
        if ((this as unknown as PlainJsonObject)._searchAppSites !== undefined) {
            return (this as unknown as PlainJsonObject)._searchAppSites as string;
        }
        if (this._config && typeof this._config.getSearchAppSitesForAppSite === 'function') {
            return this._config.getSearchAppSitesForAppSite(appSite);
        }
        return '';
    },

    // #endregion

    // #region Statistics (Non-Interface)

    /**
     * Gets resource statistics from loader's cached data
     */
    getResourceStatistics(this: LoaderNormalJson, appSite: string): ResourceStatistics {
        if (this._resourceStatsCache.has(appSite)) {
            return this._resourceStatsCache.get(appSite)!;
        }
        const stats = this._computeResourceStatistics(appSite);
        this._resourceStatsCache.set(appSite, stats);
        return stats;
    },

    /**
     * Computes resource statistics from loader's cached data
     * @private
     */
    _computeResourceStatistics(this: LoaderNormalJson, appSite: string): ResourceStatistics {
        const stats = new ResourceStatistics();

        if (this._htmlJsonCache.has(appSite)) {
            const htmlCache = this._htmlJsonCache.get(appSite)!;
            for (const [, value] of htmlCache.entries()) {
                if (value && value.jsonStructure && value.jsonStructure.size > 0) {
                    let isDefault = false;
                    if (value.jsonStructure.size === 1) {
                        const legacyHtml = value.jsonStructure.get('001_Html');
                        if (!legacyHtml.isNull && legacyHtml.asString() === this._config!.defaultReplaceHtml) {
                            isDefault = true;
                        }
                    }
                    if (!isDefault) stats.templateCount++;
                }
            }
        }

        if (this._dataJsonCache.has(appSite)) {
            const dataCache = this._dataJsonCache.get(appSite)!;
            for (const [, value] of dataCache.entries()) {
                if (value && value.jsonStructure && value.jsonStructure.size > 0) {
                    stats.jsonFileCount++;
                    (this.constructor as typeof LoaderNormalJson)._countJsonKeysAndArrays(value.jsonStructure as unknown as PlainJsonObject, stats);
                }
            }
        }

        const dynamicDataConfig = this.getDynamicDataConfig(appSite);
        if (dynamicDataConfig) {
            stats.dynamicDataHandlerCount = (this.constructor as typeof LoaderNormalJson)._countHandlersInConfig(dynamicDataConfig);
        }

        const dynamicViewConfig = this.getDynamicViewConfig(appSite);
        if (dynamicViewConfig) {
            stats.dynamicViewHandlerCount = (this.constructor as typeof LoaderNormalJson)._countHandlersInConfig(dynamicViewConfig);
        }

        return stats;
    },

    // #endregion

    // #region Private Helper Methods

    /**
     * Loads templates from a single AppSite using IConfig file path metadata
     * Optimization: If config provides preprocessed HtmlJson/DataJson (client mode), use them directly
     * Otherwise, load raw HTML/JSON and parse (server mode)
     * @private
     */
    _loadTemplatesFromSingleAppSite(this: LoaderNormalJson, config: IConfig, appSite: string): void {
        const contentNames = config.getAllContentNames(appSite);
        if (contentNames.length === 0) {
            Logger.warn(`No content names found for AppSite: ${appSite}`, 'LoaderNormalJson');
            return;
        }

        if (Logger.getLogLevel() <= LogLevel.DEBUG) {
            Logger.debug(`Loading templates for AppSite: ${appSite} (${contentNames.length} files)`, 'LoaderNormalJson');
        }

        this._store.ensureHtmlJsonAppSite(appSite);
        this._store.ensureDataJsonAppSite(appSite);

        for (const contentName of contentNames) {
            // Skip dynamic/global config entries — they are loaded separately into dedicated stores
            if (CommonUtil.isDynamicOrGlobalConfigName(contentName)) continue;

            const relativePath = config.getRelativeInfo(appSite, contentName) || '';

            // Optimization: Check if config provides preprocessed HtmlJson (client mode)
            // Bytes present means the artifact EXISTS, so a decode/parse failure is a corrupt
            // artifact — never a licence to fall through to the server-mode conversion below.
            let htmlJsonStructure: JsonObject | null = null;
            const htmlJsonBytes = config.getArtifactBytes(appSite, contentName, ArtifactKind.HtmlJson);
            if (htmlJsonBytes) {
                try {
                    htmlJsonStructure = JsonUtility.parseJsonStringStrict(JsonUtility.decodeUtf8Strict(htmlJsonBytes));
                } catch (ex) {
                    Logger.fallback('F-ENG6',
                        `HtmlJson artifact present but undecodable/unparseable: appSite=${appSite} contentName=${contentName} bytes=${htmlJsonBytes.length} ex=${(ex as Error).message}`);
                    throw ex;
                }
            }

            if (!htmlJsonStructure) {
                const htmlContent = config.getHtmlContent(appSite, contentName);
                if (htmlContent != null && config.isArtifactSource) {
                    // Raw HTML exists, so the artifact generator WOULD have emitted an HtmlJson for
                    // this template (it emits for exactly the names where getHtmlContent != null).
                    // A pre-parsed source missing it is a packaging defect: rebuilding here would
                    // hide the miss AND pay the parse cost the artifact exists to avoid. Names with
                    // no raw HTML (merge-time component-reuse placeholders such as
                    // MainContent_1_KeyMergeContent) are NOT artifact-bearing and fall through.
                    Logger.fallback('F-ENG7',
                        `HtmlJson artifact absent from a pre-parsed source: appSite=${appSite} contentName=${contentName}`);
                    throw new Error(
                        `F-ENG7: HtmlJson artifact missing for '${appSite}/${contentName}' on a pre-parsed source.`);
                }
                // Server mode: raw HTML is the ONLY source of HtmlJson, so this conversion is the
                // primary producer (the same call the artifact generator uses to BUILD artifacts).
                if (htmlContent != null) {
                    const result = GenerateHtmlJson.convertHtmlToJson(htmlContent);
                    htmlJsonStructure = result.content;

                    if (Logger.getLogLevel() <= LogLevel.DEBUG) {
                        Logger.debug(`Converted HTML to HtmlJson for ${contentName} (size: ${htmlContent.length})`, 'LoaderNormalJson');
                    }
                }
            } else {
                if (Logger.getLogLevel() <= LogLevel.DEBUG) {
                    Logger.debug(`Using preprocessed HtmlJson for ${contentName}`, 'LoaderNormalJson');
                }
            }

            if (htmlJsonStructure) {
                this._store.setHtmlJsonTemplate(appSite, contentName, {
                    relativePath: relativePath,
                    jsonStructure: htmlJsonStructure
                });
            }

            // Get parsed DataJson from IConfig (both FileConfig and ClientConfig return parsed objects)
            const dataJsonStructure = config.getDataJson(appSite, contentName);
            if (dataJsonStructure && Logger.getLogLevel() <= LogLevel.DEBUG) {
                Logger.debug(`Loaded DataJson for ${contentName} (${dataJsonStructure.size} keys)`, 'LoaderNormalJson');
            }

            if (dataJsonStructure) {
                const dataJsonKey = `${appSite.toLowerCase()}_${contentName.toLowerCase()}`;
                this._store.setDataJsonTemplate(appSite, dataJsonKey, {
                    relativePath: relativePath,
                    jsonStructure: dataJsonStructure
                });
            }
        }

        const htmlCount = this._htmlJsonCache.get(appSite)!.size;
        const dataCount = this._dataJsonCache.get(appSite)!.size;
        if (Logger.getLogLevel() <= LogLevel.DEBUG) {
            Logger.debug(`Loaded ${htmlCount} HtmlJson and ${dataCount} DataJson for ${appSite}`, 'LoaderNormalJson');
        }
    },

    // #endregion

    // #region Template Retrieval
    // Single source of truth for getTemplate lives in loaderNormalJson.ts.
    // Keep only shared helper methods in this accessor module.

    /**
     * Gets DataJson with inheritance resolution
     * @private
     */
    _getTemplateDataJsonWithInheritance(this: LoaderNormalJson, appSite: string, appView: string, appFile: string, dataJson: JsonObject, resolvedTemplateName: string, flowStack: string[] | null = null, resolvedAppSite: string | null = null, configJson: JsonObject | null = null, htmlJson: JsonObject | null = null, clientMode: boolean = false): JsonObject | null {
        const dataJsonCache = this._dataJsonCache;
        return this.getTemplateDataWithInheritance(
            appSite,
            appView,
            appFile,
            dataJson,
            resolvedTemplateName,
            dataJsonCache,
            this._config!.maxRecursionDepth,
            flowStack,
            this,
            this._appSite,
            resolvedAppSite,
            configJson,
            htmlJson,
            clientMode
        );
    },

    dumpHtmlJsonDebug(this: LoaderNormalJson, sb: string[], dict: JsonObject, indent: number): void {
        const prefix = ' '.repeat(indent * 2);
        for (const [key, value] of dict.entries()) {
            sb.push(prefix);
            sb.push(key);
            sb.push(': ');
            if (value.isObject) {
                sb.push('{');
                sb.push('\n');
                dumpHtmlJsonDebugImpl(sb, value.getObject(), indent + 1);
                sb.push(prefix);
                sb.push('}');
            } else {
                sb.push(value.toString());
            }
            sb.push('\n');
        }
    },

    // #endregion

    // #region DynamicView Helper

    /**
     * Checks if a handler should be skipped based on skip patterns
     */
    shouldSkipHandler(this: LoaderNormalJson, handlerType: string, skipPatterns: string | null): boolean {
        if (!skipPatterns) {
            return false;
        }

        // "*" means skip all handlers
        if (skipPatterns === '*') {
            return true;
        }

        // Check comma-delimited patterns (contains match, case-insensitive)
        const patterns = skipPatterns.split(',');
        const handlerLower = handlerType.toLowerCase();
        for (let i = 0; i < patterns.length; i++) {
            const pattern = patterns[i].trim();
            if (pattern && handlerLower.indexOf(pattern.toLowerCase()) >= 0) {
                return true;
            }
        }
        return false;
    },

    // #endregion

    // #region Loader State Diagnostic

    getLoaderStateJson(this: LoaderNormalJson, appSite: string): string {
        const sb: string[] = [];
        sb.push('{');

        // Template keys (union of HtmlJson + DataJson)
        sb.push('"TemplateKeys":[');
        const allKeys = new Set<string>();
        if (this._htmlJsonCache.has(appSite)) {
            for (const k of this._htmlJsonCache.get(appSite)!.keys()) allKeys.add(k);
        }
        if (this._dataJsonCache.has(appSite)) {
            for (const k of this._dataJsonCache.get(appSite)!.keys()) allKeys.add(k);
        }
        const sortedKeys = Array.from(allKeys).sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase()));
        for (let i = 0; i < sortedKeys.length; i++) {
            if (i > 0) sb.push(',');
            sb.push('"'); sb.push(sortedKeys[i]); sb.push('"');
        }
        sb.push('],');

        sb.push('"GlobalDataStore":{');
        const gdKeys = Object.keys(this._globalDataStore).sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase()));
        for (let i = 0; i < gdKeys.length; i++) {
            if (i > 0) sb.push(',');
            sb.push('"'); sb.push(gdKeys[i]); sb.push('":');
            const gd = this._globalDataStore[gdKeys[i]];
            sb.push(gd ? JsonUtility.serializeObject(gd) : 'null');
        }
        sb.push('},');

        sb.push('"GlobalViewDataStore":{');
        const gvdKeys = Object.keys(this._globalViewDataStore).sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase()));
        for (let i = 0; i < gvdKeys.length; i++) {
            if (i > 0) sb.push(',');
            sb.push('"'); sb.push(gvdKeys[i]); sb.push('":');
            const gvd = this._globalViewDataStore[gvdKeys[i]];
            sb.push(gvd ? JsonUtility.serializeObject(gvd) : 'null');
        }
        sb.push('},');

        sb.push('"GlobalViewDataComponentStore":{');
        const gvdcKeys = Object.keys(this._globalViewDataComponentStore).sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase()));
        for (let i = 0; i < gvdcKeys.length; i++) {
            if (i > 0) sb.push(',');
            sb.push('"'); sb.push(gvdcKeys[i]); sb.push('":');
            const gvdc = this._globalViewDataComponentStore[gvdcKeys[i]];
            sb.push(gvdc ? JsonUtility.serializeObject(gvdc) : 'null');
        }
        sb.push('},');

        sb.push('"DynamicDataConfigStore":{');
        const ddKeys = Object.keys(this._dynamicDataConfigStore).sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase()));
        for (let i = 0; i < ddKeys.length; i++) {
            if (i > 0) sb.push(',');
            sb.push('"'); sb.push(ddKeys[i]); sb.push('":');
            const dd = this._dynamicDataConfigStore[ddKeys[i]];
            sb.push(dd ? dd.size.toString() : 'null');
        }
        sb.push('},');

        sb.push('"DynamicViewConfigStore":{');
        const dvKeys = Object.keys(this._dynamicViewConfigStore).sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase()));
        for (let i = 0; i < dvKeys.length; i++) {
            if (i > 0) sb.push(',');
            sb.push('"'); sb.push(dvKeys[i]); sb.push('":');
            const dv = this._dynamicViewConfigStore[dvKeys[i]];
            sb.push(dv ? dv.size.toString() : 'null');
        }
        sb.push('},');

        sb.push('"DynamicActionConfigStore":{');
        const daKeys = Object.keys(this._dynamicActionConfigStore).sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase()));
        for (let i = 0; i < daKeys.length; i++) {
            if (i > 0) sb.push(',');
            sb.push('"'); sb.push(daKeys[i]); sb.push('":');
            const da = this._dynamicActionConfigStore[daKeys[i]];
            sb.push(da ? da.size.toString() : 'null');
        }
        sb.push('},');

        sb.push('"DynamicDataParamsKeys":[');
        const ddpKeys = Array.from(this._dynamicDataParamsStore.keys()).sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase()));
        for (let i = 0; i < ddpKeys.length; i++) {
            if (i > 0) sb.push(',');
            sb.push('"'); sb.push(ddpKeys[i]); sb.push('"');
        }
        sb.push('],');

        sb.push('"DynamicViewParamsKeys":[');
        const dvpKeys = Array.from(this._dynamicViewParamsStore.keys()).sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase()));
        for (let i = 0; i < dvpKeys.length; i++) {
            if (i > 0) sb.push(',');
            sb.push('"'); sb.push(dvpKeys[i]); sb.push('"');
        }
        sb.push('],');

        sb.push('"DynamicActionParamsKeys":[');
        const dapKeys = Array.from(this._dynamicActionParamsStore.keys()).sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase()));
        for (let i = 0; i < dapKeys.length; i++) {
            if (i > 0) sb.push(',');
            sb.push('"'); sb.push(dapKeys[i]); sb.push('"');
        }
        sb.push(']');

        sb.push('}');
        return sb.join('');
    },

    getLoaderPerSiteStoresJson(this: LoaderNormalJson, appSite: string): string {
        const sb: string[] = [];
        this._serializePerSiteStores(sb, appSite);
        const json = sb.join('');
        const prefix = ',"PerSiteStores":';
        return json.startsWith(prefix) ? json.substring(prefix.length) : '{}';
    }

    // #endregion
};

function dumpHtmlJsonDebugImpl(sb: string[], dict: JsonObject, indent: number): void {
    const prefix = ' '.repeat(indent * 2);
    for (const [key, value] of dict.entries()) {
        sb.push(prefix);
        sb.push(key);
        sb.push(': ');
        if (value.isObject) {
            sb.push('{');
            sb.push('\n');
            dumpHtmlJsonDebugImpl(sb, value.getObject(), indent + 1);
            sb.push(prefix);
            sb.push('}');
        } else {
            sb.push(value.toString());
        }
        sb.push('\n');
    }
}

/**
 * Static methods to be attached to LoaderNormalJson class
 */
export const accessorStaticMethods = {
    /**
     * Builds list of AppSites to load (appSite + SearchAppSites)
     */
    _buildAppSitesToLoad(appSite: string, searchAppSites: string): string[] {
        const appSitesToLoad = [appSite];
        if (searchAppSites) {
            const searchArray = searchAppSites.split(',');
            for (let i = 0; i < searchArray.length; i++) {
                const trimmed = searchArray[i].trim();
                if (!trimmed) continue;

                let found = false;
                for (let j = 0; j < appSitesToLoad.length; j++) {
                    if (appSitesToLoad[j] === trimmed) {
                        found = true;
                        break;
                    }
                }
                if (!found) {
                    appSitesToLoad.push(trimmed);
                }
            }
        }
        return appSitesToLoad;
    },

    /**
     * Recursively counts keys and arrays in a dictionary
     */
    _countJsonKeysAndArrays(dict: PlainJsonObject, stats: ResourceStatistics): void {
        for (const key of Object.keys(dict)) {
            stats.jsonKeyCount++;
            const value = dict[key];
            if (value && typeof value === 'object' && !Array.isArray(value)) {
                this._countJsonKeysAndArrays(value as PlainJsonObject, stats);
            } else if (Array.isArray(value)) {
                stats.jsonArrayCount++;
                for (const item of value) {
                    if (item && typeof item === 'object' && !Array.isArray(item)) {
                        this._countJsonKeysAndArrays(item as PlainJsonObject, stats);
                    }
                }
            }
        }
    },

    /**
     * Counts total handlers in a DynamicData/DynamicView config
     */
    _countHandlersInConfig(config: JsonObject | PlainJsonObject | DynamicConfigMap): number {
        let count = 0;
        const keys = config instanceof JsonObject ? config.keys() : Object.keys(config);
        for (const componentKey of keys) {
            const handlersArray = config instanceof JsonObject ? config.get(componentKey) : (config as PlainJsonObject)[componentKey];
            if (handlersArray) {
                if (handlersArray instanceof JsonArray) count += handlersArray.length;
                else if (Array.isArray(handlersArray)) count += handlersArray.length;
            }
        }
        return count;
    }
};
